import { useState } from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { TransactionFilters as Filters, TimePeriod } from '@/types/account-drilldown';
import type { Category } from '@metadataHooks';

interface TransactionFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  categories: Category[];
}

export function TransactionFilters({
  filters,
  onFiltersChange,
  categories,
}: TransactionFiltersProps) {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    filters.dateFrom ? new Date(filters.dateFrom) : undefined
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(
    filters.dateTo ? new Date(filters.dateTo) : undefined
  );

  const handleTimePeriodChange = (value: TimePeriod) => {
    onFiltersChange({
      ...filters,
      timePeriod: value,
      // Clear custom dates when switching to preset
      ...(value !== 'custom' && { dateFrom: undefined, dateTo: undefined }),
    });
  };

  const handleCategoryChange = (value: string) => {
    onFiltersChange({
      ...filters,
      categoryId: value === 'all' ? undefined : value,
    });
  };

  const handleDateFromChange = (date: Date | undefined) => {
    setDateFrom(date);
    onFiltersChange({
      ...filters,
      timePeriod: 'custom',
      dateFrom: date ? format(date, 'yyyy-MM-dd') : undefined,
    });
  };

  const handleDateToChange = (date: Date | undefined) => {
    setDateTo(date);
    onFiltersChange({
      ...filters,
      timePeriod: 'custom',
      dateTo: date ? format(date, 'yyyy-MM-dd') : undefined,
    });
  };

  const handleClearFilters = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    onFiltersChange({
      timePeriod: 'alltime',
      categoryId: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    });
  };

  const hasActiveFilters =
    filters.timePeriod !== 'alltime' ||
    filters.categoryId !== undefined ||
    filters.dateFrom !== undefined ||
    filters.dateTo !== undefined;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" role="search" aria-label="Transaction filters">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {/* Time Period Selector */}
        <div className="w-full sm:w-[180px]">
          <Select value={filters.timePeriod} onValueChange={handleTimePeriodChange}>
            <SelectTrigger className="min-h-[44px]" aria-label="Select time period">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last7days" className="min-h-[44px]">Last 7 days</SelectItem>
              <SelectItem value="last30days" className="min-h-[44px]">Last 30 days</SelectItem>
              <SelectItem value="last90days" className="min-h-[44px]">Last 90 days</SelectItem>
              <SelectItem value="alltime" className="min-h-[44px]">All time</SelectItem>
              <SelectItem value="custom" className="min-h-[44px]">Custom range</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Custom Date Range Pickers */}
        {filters.timePeriod === 'custom' && (
          <>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full sm:w-[180px] justify-start text-left font-normal min-h-[44px]',
                    !dateFrom && 'text-muted-foreground'
                  )}
                  aria-label="Select start date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                  {dateFrom ? format(dateFrom, 'PPP') : 'From date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={handleDateFromChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full sm:w-[180px] justify-start text-left font-normal min-h-[44px]',
                    !dateTo && 'text-muted-foreground'
                  )}
                  aria-label="Select end date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                  {dateTo ? format(dateTo, 'PPP') : 'To date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={handleDateToChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </>
        )}

        {/* Category Filter */}
        <div className="w-full sm:w-[200px]">
          <Select
            value={filters.categoryId || 'all'}
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger className="min-h-[44px]" aria-label="Filter by category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="min-h-[44px]">All categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id} className="min-h-[44px]">
                  {category.icon && `${category.icon} `}
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="w-full sm:w-auto min-h-[44px]"
          aria-label="Clear all filters"
        >
          <X className="mr-2 h-4 w-4" aria-hidden="true" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
